import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ProductCard, AvailabilityStatus, OrderStatus } from '@alkor/types';
import {
  formatPrice, formatPriceOrRequest, isPriceOnRequest, discountPercent, AVAILABILITY_LABEL, ORDER_STATUS_LABEL,
} from '@alkor/utils';
import {
  IconCart, IconCheck, IconChevron, IconBell, IconInfo, IconWarn, IconOffline, ProductArt,
} from './icons';

/* ── Статус наличия ─────────────────────────────────────────────────────── */

export function StatusChip({ status, leadTimeDays }: { status: AvailabilityStatus; leadTimeDays?: number | null }) {
  const label =
    status === 'on_order' && leadTimeDays
      ? `${AVAILABILITY_LABEL[status]} · ${leadTimeDays} дней`
      : AVAILABILITY_LABEL[status];
  return (
    <span className={`stat stat--${status}`}>
      <i />
      {label}
    </span>
  );
}

export function OrderStatusChip({ status }: { status: OrderStatus }) {
  return <span className={`schip schip--${status}`}>{ORDER_STATUS_LABEL[status]}</span>;
}

export function Badge({ product }: { product: ProductCard }) {
  const pct = discountPercent(product.price, product.oldPrice);
  if (pct) return <span className="pill pill--sale">−{pct}%</span>;
  if (product.badges.includes('new')) return <span className="pill pill--new">Новинка</span>;
  if (product.badges.includes('hit')) return <span className="pill pill--hit">Хит</span>;
  return null;
}

/* ── Изображение товара ─────────────────────────────────────────────────── */

/**
 * Изображение товара с откатом на чертёжную графику.
 * Битая или отсутствующая ссылка не должна оставлять «сломанную картинку»:
 * пока фото не залито, вместо него рисуется линейная схема категории.
 */
/**
 * Вырезанная версия фото без белого фона (готовит tools/cutout-product-images.py).
 * Лежит рядом с витриной: /cutout/<id>.png. Нет файла — показываем оригинал.
 */
export function cutoutUrl(src?: string | null): string | null {
  const m = /alkortacticalprice\.ru\/img\/([\w-]+)\.(?:jpe?g|png|webp)(?:\?.*)?$/i.exec(src ?? '');
  return m ? `/cutout/${m[1]}.png` : null;
}

export function ProductImage({
  src, alt, artKind, opacity, eager,
}: { src?: string | null; alt: string; artKind?: string | null; opacity?: number; eager?: boolean }) {
  // cutout → оригинал → чертёжная графика
  const [stage, setStage] = useState<'cutout' | 'orig' | 'broken'>(cutoutUrl(src) ? 'cutout' : 'orig');
  useEffect(() => { setStage(cutoutUrl(src) ? 'cutout' : 'orig'); }, [src]);
  if (!src || stage === 'broken') return <ProductArt kind={artKind} opacity={opacity} />;
  const cut = stage === 'cutout' ? cutoutUrl(src) : null;
  return (
    <img
      key={cut ?? src}
      className={cut ? 'pimg pimg--cutout' : 'pimg pimg--orig'}
      src={cut ?? src}
      alt={alt}
      loading={eager ? 'eager' : 'lazy'}
      decoding="async"
      onError={() => setStage(cut ? 'orig' : 'broken')}
    />
  );
}

export function Thumb({ product, artKind }: { product: ProductCard; artKind?: string }) {
  return (
    <span className="thumb">
      <ProductImage src={product.imageUrl} alt={product.name} artKind={artKind ?? product.categorySlug} />
    </span>
  );
}

/* ── Плитка товара ──────────────────────────────────────────────────────── */

export function ProductTile({
  product, inCart, onAdd, onNotify, busy,
}: {
  product: ProductCard;
  inCart: boolean;
  onAdd: (p: ProductCard) => void;
  onNotify: (p: ProductCard) => void;
  busy?: boolean;
}) {
  const navigate = useNavigate();
  const out = product.availability === 'out_of_stock';
  const onRequest = isPriceOnRequest(product.price);
  return (
    <button
      type="button"
      className={`ptile${out ? ' ptile--dim' : ''}`}
      onClick={() => navigate(`/p/${product.slug}`)}
    >
      <Thumb product={product} />
      <span className="ptile__info">
        {product.brand && <span className="ptile__brand">{product.brand}</span>}
        <span className="ptile__name">{product.name}</span>
        <span className="ptile__prices">
          {onRequest
            ? <span className="price price--request">Цена по запросу</span>
            : <span className="price">{formatPrice(product.price)}</span>}
          {!onRequest && product.oldPrice && <span className="price price--old">{formatPrice(product.oldPrice)}</span>}
        </span>
        <span style={{ display: 'block', marginTop: 6 }}>
          <StatusChip status={product.availability} leadTimeDays={product.leadTimeDays} />
        </span>
      </span>
      {(out || !onRequest) && <span
        role="button"
        tabIndex={0}
        aria-label={out ? 'Сообщить о поступлении' : 'В корзину'}
        className={`cartbtn${inCart ? ' cartbtn--in' : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          if (busy) return;
          out ? onNotify(product) : onAdd(product);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            out ? onNotify(product) : onAdd(product);
          }
        }}
      >
        {out ? <IconBell size={17} /> : inCart ? <IconCheck size={17} /> : <IconCart size={17} />}
      </span>}
    </button>
  );
}

export function RailCard({ product }: { product: ProductCard }) {
  const navigate = useNavigate();
  return (
    <button type="button" className="rcard" onClick={() => navigate(`/p/${product.slug}`)}>
      <Thumb product={product} />
      <span className="rcard__name">{product.name}</span>
      <span className={isPriceOnRequest(product.price) ? 'price price--request' : 'price'}>
        {formatPriceOrRequest(product.price)}
      </span>
    </button>
  );
}

export function Rail({ title, items, action }: { title: string; items: ProductCard[]; action?: ReactNode }) {
  if (items.length === 0) return null;
  return (
    <div className="block">
      <div className="block__head">
        <h3>{title}</h3>
        {action}
      </div>
      <div className="rail">
        {items.map((p) => <RailCard key={p.id} product={p} />)}
      </div>
    </div>
  );
}

/* ── Аккордеон ──────────────────────────────────────────────────────────── */

export function Accordion({
  title, children, defaultOpen = false,
}: { title: string; children: ReactNode; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`acc__item${open ? ' acc__item--open' : ''}`}>
      <button type="button" className="acc__btn" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        {title}
        <IconChevron size={16} />
      </button>
      {open && <div className="acc__body">{children}</div>}
    </div>
  );
}

/* ── Сегментированный контрол ───────────────────────────────────────────── */

export function Segmented<T extends string>({
  value, options, onChange,
}: {
  value: T;
  options: { value: T; label: ReactNode }[];
  onChange: (v: T) => void;
}) {
  return (
    <div className="seg" role="radiogroup">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          className={value === o.value ? 'on' : undefined}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/* ── Счётчик количества ─────────────────────────────────────────────────── */

export function Qty({
  value, onChange, disabled,
}: { value: number; onChange: (v: number) => void; disabled?: boolean }) {
  return (
    <div className="qty">
      <button type="button" aria-label="Меньше" disabled={disabled} onClick={() => onChange(value - 1)}>−</button>
      <span>{value}</span>
      <button type="button" aria-label="Больше" disabled={disabled || value >= 999} onClick={() => onChange(value + 1)}>+</button>
    </div>
  );
}

/* ── Состояния ──────────────────────────────────────────────────────────── */

export function Notice({ children, variant = 'info' }: { children: ReactNode; variant?: 'info' | 'warn' }) {
  return (
    <div className={`notice${variant === 'warn' ? ' notice--warn' : ''}`}>
      {variant === 'warn' ? <IconWarn size={16} /> : <IconInfo size={16} />}
      <span>{children}</span>
    </div>
  );
}

export function EmptyState({
  icon, title, text, action,
}: { icon: ReactNode; title: string; text: string; action?: ReactNode }) {
  return (
    <div className="empty">
      <div className="empty__icon">{icon}</div>
      <h4>{title}</h4>
      <p>{text}</p>
      {action}
    </div>
  );
}

export function TileSkeleton({ count = 4 }: { count?: number }) {
  return (
    <div className="plist" aria-hidden="true">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="ptile" style={{ cursor: 'default' }}>
          <div className="skel" style={{ width: 64, aspectRatio: '1' }} />
          <div style={{ minWidth: 0 }}>
            <div className="skel" style={{ height: 10, width: '35%' }} />
            <div className="skel" style={{ height: 13, width: '78%', marginTop: 8 }} />
            <div className="skel" style={{ height: 12, width: '42%', marginTop: 8 }} />
          </div>
          <div className="skel" style={{ width: 40, height: 40, borderRadius: 10 }} />
        </div>
      ))}
    </div>
  );
}

/** Скелетон горизонтальной ленты — той же ширины, что и настоящие карточки. */
export function RailSkeleton({ title, count = 3 }: { title: string; count?: number }) {
  return (
    <div className="block" aria-hidden="true">
      <div className="block__head"><h3>{title}</h3></div>
      <div className="rail">
        {Array.from({ length: count }, (_, i) => (
          <div key={i} className="rcard" style={{ cursor: 'default' }}>
            <div className="skel" style={{ width: '100%', aspectRatio: '4/3', marginBottom: 9 }} />
            <div className="skel" style={{ height: 11, width: '92%' }} />
            <div className="skel" style={{ height: 11, width: '64%', marginTop: 6 }} />
            <div className="skel" style={{ height: 13, width: '46%', marginTop: 10 }} />
          </div>
        ))}
      </div>
    </div>
  );
}

/** Скелетон плитки категорий. */
export function CategoriesSkeleton({ count = 6 }: { count?: number }) {
  return (
    <div className="block" aria-hidden="true">
      <div className="block__head"><h3>Категории</h3></div>
      <div className="cats">
        {Array.from({ length: count }, (_, i) => (
          <div key={i} className="cat" style={{ cursor: 'default' }}>
            <div className="skel" style={{ width: 26, height: 26, borderRadius: 8 }} />
            <div className="skel" style={{ height: 13, width: '72%' }} />
            <div className="skel" style={{ height: 10, width: '54%' }} />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Ошибка загрузки.
 * Обрыв связи и ошибка сервера — разные ситуации для пользователя: в первом
 * случае помогает он сам, во втором — только повтор, поэтому текст разный.
 */
export function ErrorState({
  message, onRetry, offline,
}: { message?: string; onRetry?: () => void; offline?: boolean }) {
  return (
    <EmptyState
      icon={offline ? <IconOffline size={24} /> : <IconWarn size={24} />}
      title={offline ? 'Нет связи' : 'Что-то пошло не так'}
      text={
        offline
          ? 'Проверьте интернет — данные загрузятся, как только соединение вернётся.'
          : message ?? 'Повторите попытку через минуту.'
      }
      action={onRetry ? <button type="button" className="secbtn" onClick={onRetry}>Попробовать снова</button> : undefined}
    />
  );
}

/** Баннер потери связи: висит поверх контента, пока сети нет. */
export function OfflineBanner({ onRetry }: { onRetry?: () => void }) {
  return (
    <div className="offline" role="status">
      <IconOffline size={16} />
      <span>Нет соединения. Показаны последние загруженные данные.</span>
      {onRetry && (
        <button type="button" onClick={onRetry}>Повторить</button>
      )}
    </div>
  );
}
